import { css } from '@emotion/react';
import { FoundationIcon, NeutralColorToken, TextButton, Type } from '@robinhood/design';
import { AddressParts } from '@x1/util-format';

import { AddressInput } from 'src/clients/Network/gql/types/address';
import Button from 'src/components/creditApplication/Button';
import Cell from 'src/components/creditApplication/Cell';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import { AddressInputToAddressType } from 'src/utils/address';

interface IPropsIface {
  error?: string;
  skippable?: boolean;
  enteredAddress?: AddressInput;
  purpose: 'residential' | 'shipping';
  onEditAddress: () => void;
  onContinue: () => void;
  loading: boolean;
}

const AddressErrorPage = ({
  error,
  skippable,
  enteredAddress,
  purpose,
  onEditAddress,
  onContinue,
  loading,
}: IPropsIface) => {
  const addressParts = enteredAddress ? AddressParts(AddressInputToAddressType(enteredAddress)) : undefined;
  const header = skippable ? 'Just double checking...' : 'Something isn’t right...';
  return (
    <CreditApplicationPageLayout
      title={header}
      subtitle={
        <>
          Please edit your {purpose} address in order to continue. For assistance contact the Robinhood Credit
          Support&nbsp;Team at:{' '}
          <TextButton
            type='anchor'
            href='mailto:apply@robinhood.com'
            variant='inline'
            label='apply@robinhood.com'
            color={NeutralColorToken.Fg1}
          />
        </>
      }
    >
      <div css={root}>
        <div css={contentContainer}>
          {!!addressParts && (
            <Cell css={cellStyles}>
              <Type.Text size='M' isBold>
                {addressParts.addressLine1}
              </Type.Text>
              <Type.Text size='M'>{addressParts.addressLine2}</Type.Text>
            </Cell>
          )}
          <div css={errorContainer}>
            <FoundationIcon type='AlertFilled16' />
            <Type.Text size='M'>{mkErrorText(error)}</Type.Text>
          </div>
        </div>
        <div css={buttonContainer}>
          <Button label='Edit address' type='button' width='full' onClick={onEditAddress} />
          {skippable && (
            <Button
              label='Continue without editing'
              type='button'
              width='full'
              onClick={onContinue}
              variant='secondary'
              state={loading ? 'loading' : false}
            />
          )}
        </div>
      </div>
    </CreditApplicationPageLayout>
  );
};

const mkErrorText = (error?: string) => {
  if (error == null) {
    return 'Something went wrong. Please try again.';
  }
  switch (error) {
    case 'NON_RESIDENTIAL_ADDRESS':
      return 'Can’t deliver to non-residential address';
    case 'RH_INVALID_ADDRESS':
      return "Can't deliver to this address";
    case 'INVALID_BUILDING`':
      return "Building can't be found";
    case 'INVALID_MULTI_UNIT':
      return "Unit can't be found";
    case 'INVALID_SUITE_NUMBER':
      return "Suite can't be found";
    case 'MISSING_OR_AMBIGUOUS_DIRECTIONAL':
      return 'Direction missing (ex. N, S, W, E, etc)';
    case 'MULTIPLE_MATCHES':
      return 'Multiple addresses found at this location';
    case 'PO_BOX':
      return 'That address looks like a P.O. box';
    case 'ZIP_CODE_NOT_FOUND':
      return 'Invalid zip code (must be 5 digits)';
    case 'INVALID_STATE':
      return 'Zip code must be from the 50 U.S states';
    default:
      return "Can't deliver to this address";
  }
};

const root = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 40px;
`;

const contentContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 22px;
`;

const cellStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const errorContainer = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const buttonContainer = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export default AddressErrorPage;
